import { Box, CircularProgress, Typography } from "@mui/material";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import React, { useCallback, useEffect, useState } from "react";
import { getDriverPerformance } from "../../../../api/DeliveryAnalyticsService"; // Update with your actual API import

export const DriverPerformanceStats = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      // Create params object for API calls
      const params = {
        start_date: startDate,
        end_date: endDate,
      };

      // Call the driver performance API
      const response = await getDriverPerformance(params);
      setData(response.data);
    } catch (err) {
      console.error("Error loading driver performance data:", err);
      setError(
        "Não foi possível carregar os dados de desempenho dos entregadores"
      );
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Take only top 8 drivers for better visualization
  const topDrivers = data.slice(0, 8);

  // Generate colors based on tax difference
  const getBarColor = (difference) => {
    if (difference > 0) return "#4CAF50"; // Profit (green)
    if (difference === 0) return "#FFC107"; // Break-even (yellow)
    return "#F44336"; // Loss (red)
  };

  // Custom formatter for currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <DirectionsBikeIcon sx={{ mr: 1, color: "primary.main" }} />
        <Typography variant="h6">Desempenho por Entregador</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Análise de entregas e taxas por entregador
        {data.length > 8 ? " (top 8 mostrados)" : ""}
      </Typography>

      <Box sx={{ height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={topDrivers}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 60,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="driverName"
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis />
            <Tooltip
              formatter={(value, name) => {
                if (name === "orderCount")
                  return [`${value} pedidos`, "Quantidade"];
                return [formatCurrency(value), name];
              }}
              labelFormatter={(value) => `Entregador: ${value}`}
            />
            <Legend
              payload={[
                { value: "Pedidos", type: "square", color: "#8884d8" },
                {
                  value: "Taxa cobrada do cliente",
                  type: "square",
                  color: "#82ca9d",
                },
                {
                  value: "Taxa paga ao entregador",
                  type: "square",
                  color: "#ffc658",
                },
                { value: "Diferença", type: "square", color: "#ff8042" },
              ]}
            />
            <Bar name="Pedidos" dataKey="orderCount" fill="#8884d8" />
            <Bar
              name="Taxa cobrada do cliente"
              dataKey="orderTaxAmount"
              fill="#82ca9d"
            />
            <Bar
              name="Taxa paga ao entregador"
              dataKey="routeTaxAmount"
              fill="#ffc658"
            />
            <Bar name="Diferença" dataKey="taxDifference">
              {topDrivers.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getBarColor(entry.taxDifference)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 2, textAlign: "center" }}
      >
        Total de entregadores no período: {data.length}
      </Typography>
    </>
  );
};
